import type { FetchedWasteTypes } from "@/Utils/api/sanity/types";
import {
  getWastetypesAvailableOnWasteRoom,
  getWastetypesInUse,
  getWastetypesNotUsed,
} from "@/components/Graphs/WasteTypesInUse/functions";
import type { AccessParentWithSortedContainers } from "@/pages/infrastructure/functions";

import type {
  GetTerminalAggregatesResponse,
  GetTerminalResponse,
} from "server/dwhApi/terminals/models.js";

import { WasteStreamUseStatus, type WasteStreamWithUse } from "./types";

type Props = {
  sanityWasteTypes: FetchedWasteTypes | null;
  data?: GetTerminalResponse;
  wasteRoomData: AccessParentWithSortedContainers[];
};

const getWasteTypesInUse = ({
  sanityWasteTypes,
  data,
  wasteRoomData,
}: Props) => {
  const inUse =
    sanityWasteTypes && data
      ? getWastetypesInUse(data.terminal, wasteRoomData, sanityWasteTypes)
      : [];

  return inUse.map((wt) => ({
    ...wt,
    status: WasteStreamUseStatus.ACTIVE,
  }));
};

const getWasteRoomUnusedWasteTypes = ({
  sanityWasteTypes,
  data,
  wasteRoomData,
  wasteTypesInUse,
}: Props & { wasteTypesInUse: { id: string }[] }) => {
  const available =
    sanityWasteTypes && data
      ? getWastetypesAvailableOnWasteRoom(
          data.terminal,
          wasteRoomData,
          sanityWasteTypes,
        )
      : [];

  return available
    .filter((wt) => !wasteTypesInUse.find((wtc) => wtc.id === wt.id))
    .map((wt) => ({
      ...wt,
      status: WasteStreamUseStatus.NOT_USED,
    }));
};

export const getWasteStreamsAtBuilding = (
  props: Props,
): WasteStreamWithUse[] => {
  const wasteTypesInUse = getWasteTypesInUse(props);

  const wasteRoomUnusedWasteTypes = getWasteRoomUnusedWasteTypes({
    ...props,
    wasteTypesInUse,
  });

  return [...wasteTypesInUse, ...wasteRoomUnusedWasteTypes];
};

export const getRecommendedWasteStreamsFromPortfolio = ({
  data,
  sanityWasteTypes,
  wasteRoomData,
  aggregatedData,
  wasteTypeClassificationSystem,
  wasteTypesAtBuilding,
}: Props & {
  aggregatedData?: GetTerminalAggregatesResponse;
  wasteTypeClassificationSystem: string;
  wasteTypesAtBuilding: WasteStreamWithUse[];
}) => {
  const recommended =
    aggregatedData && data && sanityWasteTypes
      ? getWastetypesNotUsed(
          aggregatedData.terminalAggregates,
          data.terminal,
          wasteRoomData,
          sanityWasteTypes,
          wasteTypeClassificationSystem,
        )
      : [];

  return recommended
    .filter((wt) => !wasteTypesAtBuilding.find((wtb) => wtb.id === wt.id))
    .map((wt) => ({
      ...wt,
      status: WasteStreamUseStatus.RECOMMENDED,
    }));
};
